import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useSettings } from '@/lib/settings';

export default function Footer() {
    const { settings } = useSettings();
    const year = new Date().getFullYear();
    const logo = settings?.logo_white_url || settings?.logo_url;
    // Logo height: admin-configurable (clamped 24-96 server-side). Mobile = 75% of desktop.
    const logoH = Math.max(24, Math.min(96, parseInt(settings?.logo_height, 10) || 40));
    const logoHMobile = Math.round(logoH * 0.75);

    return (
        <footer
            data-testid="site-footer"
            className="bg-[color:var(--sk-charcoal)] text-white"
        >
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                    {/* Brand */}
                    <div>
                        {logo ? (
                            <img
                                src={logo}
                                alt="Škobić Keramika"
                                className="sk-logo mb-4 brightness-0 invert"
                                style={{
                                    '--logo-h-mobile': `${logoHMobile}px`,
                                    '--logo-h-desktop': `${logoH}px`,
                                }}
                                loading="lazy"
                            />
                        ) : (
                            <div className="font-display text-2xl mb-4">Škobić Keramika</div>
                        )}
                        <p className="text-sm text-white/70 leading-relaxed">
                            Profesionalna ugradnja keramike, adaptacije kupaonica, hidroizolacija i bazeni
                            u Poreču i Istri. Kvaliteta koja traje.
                        </p>
                    </div>

                    {/* Usluge */}
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-white/60 mb-4">Usluge</p>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/ugradnja-keramike-porec" className="text-white/80 hover:text-white">
                                    Ugradnja keramike
                                </Link>
                            </li>
                            <li>
                                <Link to="/adaptacije-kupaonica-porec" className="text-white/80 hover:text-white">
                                    Adaptacije kupaonica
                                </Link>
                            </li>
                            <li>
                                <Link to="/hidroizolacija-porec" className="text-white/80 hover:text-white">
                                    Hidroizolacija
                                </Link>
                            </li>
                            <li>
                                <Link to="/keramika-za-bazene-porec" className="text-white/80 hover:text-white">
                                    Keramika za bazene
                                </Link>
                            </li>
                            <li>
                                <Link to="/ugradnja-kerlite-ploca" className="text-white/80 hover:text-white">
                                    Kerlite i veliki formati
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Stranice */}
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-white/60 mb-4">Stranice</p>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/reference" className="text-white/80 hover:text-white">Reference</Link>
                            </li>
                            <li>
                                <Link to="/blog" className="text-white/80 hover:text-white">Blog</Link>
                            </li>
                            <li>
                                <Link to="/o-nama" className="text-white/80 hover:text-white">O nama</Link>
                            </li>
                            <li>
                                <Link to="/kontakt" className="text-white/80 hover:text-white">Kontakt</Link>
                            </li>
                        </ul>
                    </div>

                    {/* Kontakt */}
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-white/60 mb-4">Kontakt</p>
                        <ul className="space-y-3 text-sm">
                            <li className="flex items-start gap-2">
                                <Phone className="h-4 w-4 text-[color:var(--sk-accent)] mt-0.5 shrink-0" />
                                <a
                                    href={settings?.phone_link || 'tel:+385977094158'}
                                    className="text-white/90 hover:text-white"
                                    data-testid="footer-phone-link"
                                >
                                    {settings?.phone || '+385 97 709 4158'}
                                </a>
                            </li>
                            <li className="flex items-start gap-2">
                                <Mail className="h-4 w-4 text-[color:var(--sk-accent)] mt-0.5 shrink-0" />
                                <a
                                    href={`mailto:${settings?.email || 'skobickeramika@gmail.com'}`}
                                    className="text-white/90 hover:text-white"
                                    data-testid="footer-email-link"
                                >
                                    {settings?.email || 'skobickeramika@gmail.com'}
                                </a>
                            </li>
                            <li className="flex items-start gap-2">
                                <MapPin className="h-4 w-4 text-[color:var(--sk-accent)] mt-0.5 shrink-0" />
                                <span className="text-white/90">{settings?.address || 'Poreč, Istra, Hrvatska'}</span>
                            </li>
                        </ul>
                        {settings?.working_hours && (
                            <p className="mt-4 text-xs text-white/60 leading-relaxed">
                                <span className="uppercase tracking-[0.18em]">Radno vrijeme</span>
                                <br />
                                {settings.working_hours}
                            </p>
                        )}
                    </div>
                </div>

                {/* Bottom row */}
                <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <p className="text-xs text-white/60">
                        © {year} {settings?.site_name || 'Škobić Keramika'}. Sva prava pridržana.
                    </p>
                    <div className="flex items-center gap-6 text-xs text-white/60">
                        <Link to="/privatnost" className="hover:text-white">Privatnost</Link>
                        <Link to="/uvjeti" className="hover:text-white">Uvjeti</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
